import { useState, useEffect, useCallback } from "react";
import { listCargoAgencies, entriesForInvoice, listCargoInvoices, createCargoInvoice, deleteCargoInvoice, downloadCargoInvoiceXlsx } from "@/lib/api";
import { useRefreshOnFocus } from "@/hooks/useRefreshOnFocus";
import { FileText, Trash2, Plus, Loader2, FileSpreadsheet, Plane, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const eur = (n) => `€ ${Number(n || 0).toFixed(2)}`;
const partyLabel = (p) => (p === "airline" ? "Airline" : "Cargo Agency");
const partyServices = (e, party) => (e.services || []).filter((s) => (s.party || "cargo_agency") === party);
const partyTotal = (e, party) => partyServices(e, party).reduce((s, x) => s + Number(x.amount || 0), 0);

export default function CargoInvoicingPage() {
  const [agencies, setAgencies] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [party, setParty] = useState("cargo_agency");
  const [agencyName, setAgencyName] = useState("");
  const [month, setMonth] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [vat, setVat] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadInvoices = useCallback(async () => {
    try { const r = await listCargoInvoices(); const rows = r.data || []; rows.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))); setInvoices(rows); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    listCargoAgencies().then((r) => setAgencies(r.data || [])).catch(() => {});
    loadInvoices();
  }, [loadInvoices]);
  useRefreshOnFocus(loadInvoices);

  const loadCandidates = async () => {
    if (!agencyName) { toast.error("Choose an agency first"); return; }
    setLoadingCand(true);
    try {
      const r = await entriesForInvoice(agencyName, month, party);
      const rows = r.data || [];
      // newest shipments first
      rows.sort((a, b) => String(b.shipment_date || "").localeCompare(String(a.shipment_date || "")));
      setCandidates(rows);
      // pre-select everything that has prices for the chosen party
      setPicked(new Set(rows.filter((e) => partyServices(e, party).length > 0).map((e) => e.id)));
    } catch { toast.error("Failed to load cargo"); }
    finally { setLoadingCand(false); }
  };

  const toggle = (id) => setPicked((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const pickedEntries = candidates.filter((e) => picked.has(e.id));
  const wantType = party === "airline" ? "airline" : "agency";
  const filteredAgencies = agencies.filter((a) => !a.type || a.type === wantType);
  const subtotal = pickedEntries.reduce((s, e) => s + partyTotal(e, party), 0);
  const vatAmount = subtotal * Number(vat || 0) / 100;
  const total = subtotal + vatAmount;

  const save = async () => {
    if (!agencyName) { toast.error("Choose an agency"); return; }
    if (pickedEntries.length === 0) { toast.error("Select at least one AWB"); return; }
    setSaving(true);
    try {
      const payload = {
        party,
        agency_name: agencyName,
        agency_id: agencies.find((a) => a.name === agencyName)?.id || "",
        month,
        vat_percent: Number(vat || 0),
        line_items: pickedEntries.map((e) => ({
          entry_id: e.id,
          shipment_id: e.shipment_id,
          flight_no: e.flight_no || "",
          origin: e.origin || "",
          arr_date: e.shipment_date || "",
          pickup_date: e.storage_out_date || e.shipment_date || "",
          weight_kg: e.weight_kg || "",
          pieces: e.pieces || "",
          storage_days: e.storage_days || 0,
          operator: e.operator || e.consignee || "",
          shipment_date: e.shipment_date,
          description: partyServices(e, party).map((s) => s.name).join(", "),
          services: partyServices(e, party).map((s) => ({ name: s.name, amount: Number(s.amount || 0) })),
          amount: partyTotal(e, party),
        })),
      };
      const r = await createCargoInvoice(payload);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      setPicked(new Set()); setCandidates([]); setVat(0);
      loadInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save invoice"); }
    finally { setSaving(false); }
  };

  const downloadXlsx = async (inv) => {
    setDownloadingId(inv.id);
    try {
      const r = await downloadCargoInvoiceXlsx(inv.id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proforma_${partyLabel(inv.party).replace(/\s/g, "")}_${(inv.invoice_number || "invoice").replace(/\//g, "-")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download XLS"); }
    finally { setDownloadingId(null); }
  };

  const confirmDelete = async () => {
    try { await deleteCargoInvoice(deleteId); toast.success("Invoice deleted"); setDeleteId(null); loadInvoices(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="cargo-invoicing-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="text-emerald-500" /> Cargo Invoicing
        </h1>
        <p className="text-slate-500 text-sm mt-1">Choose who you're billing (Airline or Cargo Agency), pick the agency &amp; month, select the AWBs, then create the proforma XLS.</p>
      </div>

      {/* Builder */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
        {/* Step 1: who are we billing? */}
        <div>
          <Label className="text-xs text-slate-500">Invoice to</Label>
          <div className="mt-1 grid grid-cols-2 gap-2 max-w-md" data-testid="invoice-party-toggle">
            <button
              onClick={() => { setParty("cargo_agency"); setAgencyName(""); setCandidates([]); setPicked(new Set()); }}
              className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${party === "cargo_agency" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-emerald-300"}`}
              data-testid="invoice-party-cargo_agency">
              <Warehouse size={16} /> Cargo Agency
            </button>
            <button
              onClick={() => { setParty("airline"); setAgencyName(""); setCandidates([]); setPicked(new Set()); }}
              className={`flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${party === "airline" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-blue-300"}`}
              data-testid="invoice-party-airline">
              <Plane size={16} /> Airline
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">{party === "airline" ? "Airline" : "Agency"}</Label>
            <select value={agencyName} onChange={(e) => setAgencyName(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="invoice-agency-select">
              <option value="">Select {party === "airline" ? "airline" : "agency"}…</option>
              {filteredAgencies.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1" data-testid="invoice-month" />
          </div>
          <div className="flex items-end">
            <Button onClick={loadCandidates} disabled={loadingCand} className="bg-slate-800 hover:bg-slate-900 text-white w-full" data-testid="invoice-load-cargo-btn">
              {loadingCand ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Load Cargo
            </Button>
          </div>
        </div>

        {candidates.length > 0 && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200">
              <span className="text-xs text-slate-500">{picked.size} of {candidates.length} selected</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPicked(new Set(candidates.map((e) => e.id)))} data-testid="invoice-select-all-btn">Select all</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPicked(new Set())} data-testid="invoice-clear-btn">Clear</Button>
              </div>
            </div>
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 px-3">Pick</th>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">AWB</th>
                    <th className="py-2 px-3">Flight</th>
                    <th className="py-2 px-3">{partyLabel(party)} services</th>
                    <th className="py-2 px-3">Kg</th>
                    <th className="py-2 px-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody data-testid="invoice-candidates">
                  {candidates.map((e) => {
                    const svcs = partyServices(e, party);
                    const amt = partyTotal(e, party);
                    return (
                      <tr key={e.id} className={`border-b border-slate-100 ${svcs.length === 0 ? "opacity-60" : ""}`}>
                        <td className="py-2 px-3"><input type="checkbox" checked={picked.has(e.id)} onChange={() => toggle(e.id)} className="w-4 h-4 accent-emerald-600" data-testid={`invoice-pick-${e.id}`} /></td>
                        <td className="py-2 px-3 whitespace-nowrap">{e.shipment_date || "—"}</td>
                        <td className="py-2 px-3 font-mono">{e.shipment_id}</td>
                        <td className="py-2 px-3 whitespace-nowrap">{e.flight_no || "—"}</td>
                        <td className="py-2 px-3 max-w-[240px] truncate text-slate-600">
                          {svcs.length ? svcs.map((s) => s.name).join(", ") : <span className="text-slate-400 italic">no {partyLabel(party)} prices</span>}
                        </td>
                        <td className="py-2 px-3">{e.weight_kg || "—"}</td>
                        <td className="py-2 px-3 font-medium text-right" data-testid={`invoice-amount-${e.id}`}>{eur(amt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pickedEntries.length > 0 && (
          <div className="flex flex-col items-end gap-2 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <Label className="text-xs text-slate-500">VAT %</Label>
              <Input type="number" value={vat} onChange={(e) => setVat(e.target.value)} className="h-9 w-24" data-testid="invoice-vat" />
            </div>
            <div className="text-sm text-slate-600">Subtotal: <span className="font-semibold text-slate-900" data-testid="invoice-subtotal">{eur(subtotal)}</span></div>
            <div className="text-sm text-slate-600">VAT: <span className="font-semibold text-slate-900">{eur(vatAmount)}</span></div>
            <div className="text-lg font-bold text-slate-900">Total: <span data-testid="invoice-total">{eur(total)}</span></div>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invoice-save-btn">
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />} Create {partyLabel(party)} Invoice
            </Button>
          </div>
        )}
      </div>

      {/* Existing invoices */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200"><h2 className="font-semibold text-slate-900">Invoices</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="py-3 px-4">Number</th>
                <th className="py-3 px-4">Party</th>
                <th className="py-3 px-4">Agency</th>
                <th className="py-3 px-4">Month</th>
                <th className="py-3 px-4">AWBs</th>
                <th className="py-3 px-4">Subtotal</th>
                <th className="py-3 px-4">VAT</th>
                <th className="py-3 px-4">Total</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="invoices-tbody">
              {invoices.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-slate-400">No invoices yet.</td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2.5 px-4 font-mono font-semibold text-emerald-700">{inv.invoice_number}</td>
                  <td className="py-2.5 px-4">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${inv.party === "airline" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {inv.party === "airline" ? <Plane size={12} /> : <Warehouse size={12} />} {partyLabel(inv.party)}
                    </span>
                  </td>
                  <td className="py-2.5 px-4">{inv.agency_name}</td>
                  <td className="py-2.5 px-4">{inv.month || "—"}</td>
                  <td className="py-2.5 px-4">{inv.line_items?.length || 0}</td>
                  <td className="py-2.5 px-4">{eur(inv.subtotal)}</td>
                  <td className="py-2.5 px-4">{inv.vat_percent}% ({eur(inv.vat_amount)})</td>
                  <td className="py-2.5 px-4 font-semibold">{eur(inv.total)}</td>
                  <td className="py-2.5 px-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => downloadXlsx(inv)} disabled={downloadingId === inv.id} className="h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`download-invoice-${inv.id}`}>
                        {downloadingId === inv.id ? <Loader2 className="animate-spin mr-1.5" size={14} /> : <FileSpreadsheet size={14} className="mr-1.5" />} XLS
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(inv.id)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`delete-invoice-${inv.id}`}><Trash2 size={16} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-invoice-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
